import { inject } from '@angular/core';
import type { HttpInterceptorFn } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ReporteErroresService } from '../errores/reporte-errores';
import type { ApiErrorBody, NormalizedApiError } from '../models/api-response.model';

// Normaliza todo error HTTP a NormalizedApiError. El `message` del backend ya viene en
// español y se muestra tal cual (RN-UI-06).
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const reporte = inject(ReporteErroresService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) return throwError(() => error);

      const body = error.error as Partial<ApiErrorBody> | null;
      const normalizado: NormalizedApiError = {
        status: error.status,
        code: body?.error?.code ?? 'ERROR_DESCONOCIDO',
        message: body?.error?.message ?? 'Ocurrió un error inesperado.',
        details: body?.error?.details,
      };

      // D-159: al registro de errores lo que el servidor no pudo anotar: sin respuesta (con
      // internet) o el servidor caído. Los 500 ya los guarda el propio servidor.
      const api = `${req.method} ${req.url.replace(environment.apiUrl, '').split('?')[0]}`;
      if (error.status === 0 && navigator.onLine) reporte.reportar({ tipo: 'RED', mensaje: 'La petición no obtuvo respuesta', api, estado_http: 0 });
      if (error.status === 502 || error.status === 503 || error.status === 504) reporte.reportar({ tipo: 'API', mensaje: `El servidor respondió ${error.status}`, api, estado_http: error.status });

      if (error.status === 0) {
        normalizado.code = 'SIN_CONEXION';
        normalizado.message = 'No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.';
      }

      // Permisos de dinero sin 2FA activo (SPEC RN-ROL-02) → configurar 2FA
      if (error.status === 403 && normalizado.code === 'REQUIERE_2FA') {
        router.navigate(['/admin/seguridad/2fa'], { queryParams: { motivo: 'requerido' } });
      }

      // El servidor dice cuál límite fue y cuánto esperar; si no trae mensaje, uno general
      if (error.status === 429 && !body?.error?.message) {
        normalizado.message = 'Demasiadas solicitudes seguidas. Espera unos minutos y vuelve a intentar.';
      }

      return throwError(() => normalizado);
    }),
  );
};
